import { useCallback, useState, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { Rnd, type DraggableData, type RndResizeCallback } from 'react-rnd';
import type { LucideIcon } from 'lucide-react';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { OVERLAY_PANEL_COLLAPSED_HEIGHT_PX } from '@/shared/config';
import { Button } from '@/shared/ui/shadcn/button';

import { OVERLAY_PANEL_RESIZE_HANDLE_CLASSES, OVERLAY_PANEL_RESIZE_HANDLE_STYLES } from './lib';

export interface Position {
  x: number;
  y: number;
}

export interface Size {
  width: number | string;
  height: number | string;
}

export interface OverlayPanelProps {
  headerIcon?: LucideIcon;
  headerIconLabel?: string;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;

  bounds?: 'parent' | 'window' | 'body' | string | HTMLElement;

  collapsible?: boolean;
  defaultCollapsed?: boolean;

  defaultPosition?: Position;

  defaultSize?: Size;

  minWidth?: number | string;
  minHeight?: number | string;
  maxWidth?: number | string;
  maxHeight?: number | string;
}

const DRAG_HANDLE_CLASS = 'overlay-panel-drag-handle';
const DEFAULT_POSITION: Position = { x: 0, y: 0 };
const DEFAULT_SIZE: Size = { width: 360, height: 420 };

export function OverlayPanel({
  headerIcon: HeaderIcon,
  headerIconLabel,
  actions,
  bounds = 'window',
  collapsible = false,
  defaultCollapsed = false,
  defaultPosition = DEFAULT_POSITION,
  defaultSize = DEFAULT_SIZE,
  minWidth = 240,
  minHeight = 160,
  maxWidth,
  maxHeight,
  className,
  children,
}: OverlayPanelProps) {
  const shouldReduceMotion = useReducedMotion();
  const hasHeader = HeaderIcon !== undefined || actions !== undefined;

  const [internalCollapsed, setInternalCollapsed] = useState<boolean>(defaultCollapsed);
  const [internalPosition, setInternalPosition] = useState<Position>(defaultPosition);
  const [internalSize, setInternalSize] = useState<Size>(defaultSize);
  const [isResizing, setIsResizing] = useState(false);

  const collapsed = collapsible && internalCollapsed;
  const position = internalPosition;
  const size = internalSize;

  const handleDragStop = useCallback((_event: unknown, data: DraggableData) => {
    const nextPosition: Position = { x: data.x, y: data.y };
    setInternalPosition(nextPosition);
  }, []);

  const handleResizeStop = useCallback<RndResizeCallback>(
    (_event, _direction, ref, _delta, nextPosition) => {
      const nextSize: Size = { width: ref.offsetWidth, height: ref.offsetHeight };
      const nextPos: Position = { x: nextPosition.x, y: nextPosition.y };

      setInternalSize(nextSize);
      setInternalPosition(nextPos);
    },
    []
  );

  const handleResizeStart = useCallback(() => {
    setIsResizing(true);
  }, []);

  const handleResizeStopWithState = useCallback<RndResizeCallback>(
    (...args) => {
      setIsResizing(false);
      handleResizeStop(...args);
    },
    [handleResizeStop]
  );

  const handleToggleCollapsed = useCallback(() => {
    if (!collapsible) {
      return;
    }
    setInternalCollapsed(!collapsed);
  }, [collapsed, collapsible]);

  const effectiveSize: Size = collapsed
    ? { width: size.width, height: OVERLAY_PANEL_COLLAPSED_HEIGHT_PX }
    : size;

  const panel = (
    <motion.div
      role="region"
      className={cn(
        'pointer-events-auto flex h-full min-h-0 min-w-0 flex-col overflow-hidden',
        'rounded-lg border border-border shadow-lg',
        'bg-card/80 backdrop-blur supports-[backdrop-filter]:bg-card/80',
        className
      )}
      initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.98, y: 8 }}
      animate={shouldReduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.44, ease: [0.44, 1, 0.36, 1] }}
    >
      {hasHeader ? (
        <div
          className={cn(
            DRAG_HANDLE_CLASS,
            'flex items-center justify-between gap-2 px-3 py-2',
            collapsed ? 'border-b-0' : 'border-b',
            'cursor-grab select-none active:cursor-grabbing'
          )}
          data-slot="overlay-panel-header"
        >
          <div className="min-w-0 flex flex-1 items-center">
            {HeaderIcon ? (
              <div
                className="inline-flex items-center"
                title={headerIconLabel ?? 'Panel'}
                aria-label={headerIconLabel ?? 'Panel'}
              >
                <HeaderIcon className="size-4" aria-hidden />
              </div>
            ) : null}
          </div>
          {collapsible || actions !== undefined ? (
            <div className="flex shrink-0 items-center gap-1" data-slot="overlay-panel-actions">
              {collapsible ? (
                <Button
                  aria-label={collapsed ? 'Restore' : 'Minimize'}
                  size="icon-sm"
                  type="button"
                  variant="ghost"
                  onClick={handleToggleCollapsed}
                >
                  {collapsed ? (
                    <ChevronUpIcon className="size-4" />
                  ) : (
                    <ChevronDownIcon className="size-4" />
                  )}
                </Button>
              ) : null}
              {actions}
            </div>
          ) : null}
        </div>
      ) : null}

      {!collapsed ? (
        <div className="min-h-0 min-w-0 flex-1 overflow-auto p-3" data-slot="overlay-panel-body">
          {children}
        </div>
      ) : (
        <div
          className="min-h-0 min-w-0 flex-1 overflow-hidden p-3"
          data-slot="overlay-panel-body"
          aria-hidden="true"
        >
          {children}
        </div>
      )}
    </motion.div>
  );

  return (
    <Rnd
      bounds={bounds}
      className={cn(
        isResizing ? 'transition-none' : 'transition-[width,height] duration-200 ease-out',
        'motion-reduce:transition-none'
      )}
      dragHandleClassName={DRAG_HANDLE_CLASS}
      enableResizing={
        collapsed
          ? false
          : {
              top: true,
              right: true,
              bottom: true,
              left: true,
              topRight: true,
              bottomRight: true,
              bottomLeft: true,
              topLeft: true,
            }
      }
      resizeHandleWrapperClass="pointer-events-auto"
      resizeHandleClasses={OVERLAY_PANEL_RESIZE_HANDLE_CLASSES}
      resizeHandleStyles={OVERLAY_PANEL_RESIZE_HANDLE_STYLES}
      maxHeight={collapsed ? OVERLAY_PANEL_COLLAPSED_HEIGHT_PX : maxHeight}
      maxWidth={maxWidth}
      minHeight={collapsed ? OVERLAY_PANEL_COLLAPSED_HEIGHT_PX : minHeight}
      minWidth={minWidth}
      onDragStop={handleDragStop}
      onResizeStart={handleResizeStart}
      onResizeStop={handleResizeStopWithState}
      position={position}
      size={effectiveSize}
      style={{ position: 'fixed' }}
    >
      {panel}
    </Rnd>
  );
}
